import {
  Users, Mail, Hash, Phone, MessageCircle, Package, Edit2, Check, X,
  ChevronDown, ChevronUp, Zap, Crown, ShoppingBag, UserPlus, FileText, Eye
} from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { getUserTagType, TAG_CONFIG, isUserLive, getRelativeTime, openWhatsApp, openEmail, WHATSAPP_MESSAGE } from "./adminHelpers";

const TAG_ICONS = { vip: Crown, purchased: ShoppingBag, new: UserPlus };

export const UserManagementTable = ({
  users, editingUser, editForm, setEditForm, isSaving,
  onStartEdit, onSaveEdit, onCancelEdit,
  onToggleTrialKit, onMarkWhatsAppContacted,
  onSelectUser,
  sortField, sortDirection, onToggleSort,
}) => {
  return (
    <div data-testid="admin-users-table" className="bg-white dark:bg-[#1a231a] border border-[#2C3329]/10 dark:border-[#2a3a2a] rounded-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#FAF8F5] dark:bg-[#121812] border-b border-[#2C3329]/10 dark:border-[#2a3a2a]">
              <Th>משתמש</Th>
              <Th>סטטוס</Th>
              <Th center>ערכת ניסיון</Th>
              <Th center>PDF</Th>
              <Th className="hidden xl:table-cell">קופון</Th>
              <Th>טלפון</Th>
              <ThSortable label="פעילות" field="last_login" sortField={sortField} sortDirection={sortDirection} onToggle={onToggleSort} className="hidden lg:table-cell" />
              <Th>פעולות</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2C3329]/5 dark:divide-[#2a3a2a]">
            {users.map((u) => (
              <UserRow
                key={u.user_id}
                u={u}
                editingUser={editingUser}
                editForm={editForm}
                setEditForm={setEditForm}
                isSaving={isSaving}
                onStartEdit={onStartEdit}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
                onToggleTrialKit={onToggleTrialKit}
                onMarkWhatsAppContacted={onMarkWhatsAppContacted}
                onSelectUser={onSelectUser}
              />
            ))}
          </tbody>
        </table>
      </div>
      {users.length === 0 && (
        <div className="text-center py-16">
          <Users className="h-10 w-10 text-[#A3A3A3] mx-auto mb-3" />
          <p className="font-sans text-sm text-[#5C5C5C]">לא נמצאו משתמשים</p>
        </div>
      )}
    </div>
  );
};

const Th = ({ children, center, className = "" }) => (
  <th className={`text-${center ? 'center' : 'right'} px-4 py-3 font-sans text-[11px] tracking-[0.05em] uppercase text-[#5C5C5C] font-medium ${className}`}>
    {children}
  </th>
);

const ThSortable = ({ label, field, sortField, sortDirection, onToggle, className = "" }) => (
  <th className={`text-right px-4 py-3 font-sans text-[11px] tracking-[0.05em] uppercase text-[#5C5C5C] font-medium ${className}`}>
    <button onClick={() => onToggle(field)} className="flex items-center gap-1 hover:text-[#2C3329] transition-colors">
      {label}
      {sortField === field && (sortDirection === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
    </button>
  </th>
);

const UserRow = ({ u, editingUser, editForm, setEditForm, isSaving, onStartEdit, onSaveEdit, onCancelEdit, onToggleTrialKit, onMarkWhatsAppContacted, onSelectUser }) => {
  const tagType = getUserTagType(u);
  const tag = TAG_CONFIG[tagType];
  const TagIcon = TAG_ICONS[tagType];
  const live = isUserLive(u.last_login);
  const isEditing = editingUser === u.user_id;

  return (
    <tr className="hover:bg-[#FAF8F5]/60 dark:hover:bg-[#121812]/50 transition-colors group">
      {/* User */}
      <td className="px-4 py-3">
        <button onClick={() => onSelectUser(u)} className="flex items-center gap-3 text-right group/user" data-testid={`user-row-${u.user_id}`}>
          <div className="relative">
            <Avatar className="h-9 w-9 border border-[#2C3329]/10">
              <AvatarImage src={u.picture} alt={u.name} />
              <AvatarFallback className="bg-[#4A5D43]/10 text-[#4A5D43] font-sans text-sm">{u.name?.charAt(0) || '?'}</AvatarFallback>
            </Avatar>
            {live && <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#6B8E6B] rounded-full border-2 border-white dark:border-[#1a231a]" />}
          </div>
          <div className="min-w-0">
            <p className="font-sans text-sm font-medium text-[#2C3329] dark:text-white truncate group-hover/user:text-[#4A5D43] transition-colors">{u.name || 'ללא שם'}</p>
            <p className="font-sans text-xs text-[#A3A3A3] truncate">{u.email}</p>
          </div>
        </button>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium ${tag.bg} ${tag.text}`}>
          {TagIcon && <TagIcon className="h-3 w-3" />}
          {tag.label}
        </span>
      </td>

      {/* Trial Kit Toggle */}
      <td className="px-4 py-3 text-center">
        <button
          data-testid={`toggle-kit-${u.user_id}`}
          onClick={() => onToggleTrialKit(u.user_id, !(isEditing ? editForm.received_trial_kit : u.received_trial_kit))}
          className={`relative w-9 h-5 rounded-full transition-colors ${(isEditing ? editForm.received_trial_kit : u.received_trial_kit) ? 'bg-[#4A5D43]' : 'bg-[#2C3329]/15'}`}
        >
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${(isEditing ? editForm.received_trial_kit : u.received_trial_kit) ? 'right-0.5' : 'right-[18px]'}`} />
        </button>
      </td>

      {/* PDF */}
      <td className="px-4 py-3 text-center">
        {u.pdf_download_count > 0 ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-[#7B8C96]/10 text-[#7B8C96]">
            <FileText className="h-3 w-3" />{u.pdf_download_count}x
          </span>
        ) : <span className="text-xs text-[#A3A3A3]">—</span>}
      </td>

      {/* Coupon */}
      <td className="px-4 py-3 hidden xl:table-cell">
        {u.coupon_code ? (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium ${u.coupon_used ? 'bg-[#6B8E6B]/10 text-[#6B8E6B]' : 'bg-[#C67D63]/10 text-[#C67D63]'}`}>
            <Hash className="h-3 w-3" />{u.coupon_code}
          </span>
        ) : <span className="text-xs text-[#A3A3A3]">—</span>}
      </td>

      {/* Phone */}
      <td className="px-4 py-3">
        {isEditing ? (
          <input
            data-testid={`edit-phone-input-${u.user_id}`}
            type="tel"
            value={editForm.phone}
            onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
            className="w-28 px-2 py-1 font-sans text-sm rounded-sm border border-[#4A5D43]/40 bg-white dark:bg-[#121812] focus:outline-none focus:ring-1 focus:ring-[#4A5D43]"
            placeholder="050-0000000"
            dir="ltr"
          />
        ) : (
          <span className="font-sans text-sm text-[#5C5C5C] dark:text-[#9A9790]" dir="ltr">{u.phone || '—'}</span>
        )}
      </td>

      {/* Last Activity */}
      <td className="px-4 py-3 hidden lg:table-cell">
        {live ? (
          <span className="text-[#6B8E6B] text-sm font-medium flex items-center gap-1">
            <Zap className="h-3 w-3" />כרגע באתר
          </span>
        ) : (
          <span className="font-sans text-sm text-[#A3A3A3]">{getRelativeTime(u.last_login)}</span>
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-0.5">
          {isEditing ? (
            <>
              <ActionBtn testId={`save-edit-${u.user_id}`} icon={Check} color="text-[#6B8E6B] hover:bg-[#6B8E6B]/10" onClick={() => onSaveEdit(u.user_id)} disabled={isSaving} />
              <ActionBtn testId={`cancel-edit-${u.user_id}`} icon={X} color="text-[#C67D63] hover:bg-[#C67D63]/10" onClick={onCancelEdit} />
            </>
          ) : (
            <>
              {u.phone && (
                <ActionBtn
                  testId={`user-row-whatsapp-btn-${u.user_id}`}
                  icon={MessageCircle}
                  color="text-[#6B8E6B] hover:bg-[#6B8E6B]/10"
                  onClick={() => { openWhatsApp(u.phone, WHATSAPP_MESSAGE); onMarkWhatsAppContacted(u.user_id); }}
                  title="WhatsApp"
                />
              )}
              <ActionBtn testId={`user-row-email-btn-${u.user_id}`} icon={Mail} color="text-[#7B8C96] hover:bg-[#7B8C96]/10" onClick={() => openEmail(u.email, u.name)} title="אימייל" />
              <ActionBtn testId={`user-row-edit-btn-${u.user_id}`} icon={Edit2} color="text-[#5C5C5C] hover:bg-[#2C3329]/5" onClick={() => onStartEdit(u)} title="עריכה" />
              <ActionBtn testId={`user-row-details-btn-${u.user_id}`} icon={Eye} color="text-[#5C5C5C] hover:bg-[#2C3329]/5 opacity-0 group-hover:opacity-100" onClick={() => onSelectUser(u)} title="פרטים" />
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

const ActionBtn = ({ testId, icon: Icon, color, onClick, disabled, title }) => (
  <Button data-testid={testId} size="sm" variant="ghost" onClick={onClick} disabled={disabled} title={title} className={`h-8 w-8 p-0 rounded-sm transition-all ${color}`}>
    <Icon className="h-3.5 w-3.5" />
  </Button>
);
